import { supabase } from '../supabase';
import { verseKey, compareVerses, NOTE_MAX } from './anchors';
import { sanitizeRecord, mergeRecords, adoptRecords } from './merge';
import { uuid } from './uuid';

// ── The reader's highlights and notes ────────────────────────────────────────
// One store for the whole app, outside React, so the Reading page and The
// Bible share it and only one sync runs however many pages mount it. Every
// change is written to localStorage at once — marks work signed out and
// offline — and, when signed in, sent to the account a moment later.
//
// localStorage holds one list per owner: "guest" for signed-out marks and one
// per account id, so two people sharing a device never see each other's notes.
// On sign-in the guest list is folded into the account and cleared (owner's
// decision, 2026-10-10).

const LIST_KEY  = owner => `bibleAnnotations:${owner}`;
const SINCE_KEY = owner => `bibleAnnotationsSince:${owner}`;
const GUEST = 'guest';

const PUSH_DELAY  = 1500;   // batch a burst of taps into one write
const PUSH_CHUNK  = 200;    // records per save call (the server refuses more)
const PAGE_SIZE   = 1000;   // records per list call (the server's own limit)
const REFETCH_GAP = 15000;  // at most one fetch per 15 s from focus changes
const RETRY_GAP   = 60000;  // after a failure, wait before trying again
// Fetches ask from a little before the last row seen, so a row whose write
// committed late is still picked up; the merge makes the overlap harmless.
const OVERLAP_MS  = 5 * 60 * 1000;
// A delete that every device has had long enough to hear about.
const TOMBSTONE_TTL = 90 * 24 * 3600 * 1000;

let loaded = false;         // the first setAccount reads localStorage
let owner = GUEST;
let account = null;          // { id, email } when signed in
let records = [];
let snapshot = buildSnapshot([]);
const listeners = new Set();

let pushTimer = null;
let syncing = null;          // the running sync, so calls queue behind it
let again = false;           // a change arrived mid-sync; run once more
let lastFetch = 0;
let retryAfter = 0;
let warned = false;

// ── Reading and writing localStorage ─────────────────────────────────────────
function load(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function save(key, value) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // Full or blocked storage: the marks still live for this visit.
    console.warn('annotations: could not save locally', err);
  }
}
function loadList(who) {
  const list = load(LIST_KEY(who));
  if (!Array.isArray(list)) return [];
  const cutoff = Date.now() - TOMBSTONE_TTL;
  return list
    .map(r => { const s = sanitizeRecord(r); return s && (r._dirty ? { ...s, _dirty: true } : s); })
    .filter(r => r && !(r.deleted && !r._dirty && Date.parse(r.updated_at) < cutoff));
}

// ── Snapshot for React ───────────────────────────────────────────────────────
// What the pages read: live records by verse (oldest first, so the newest
// draws on top), each group's records, and which record carries a group's
// note icon — the last verse of the group in reading order.
function buildSnapshot(list) {
  const byVerse = new Map();
  const groups = new Map();
  for (const r of list) {
    if (r.deleted) continue;
    const k = verseKey(r.book, r.chapter, r.verse, r.lang);
    if (!byVerse.has(k)) byVerse.set(k, []);
    byVerse.get(k).push(r);
    if (!groups.has(r.group_id)) groups.set(r.group_id, []);
    groups.get(r.group_id).push(r);
  }
  const byTime = (a, b) => Date.parse(a.updated_at) - Date.parse(b.updated_at) || (a.id < b.id ? -1 : 1);
  byVerse.forEach(v => v.sort(byTime));
  const noteEnds = new Set();
  groups.forEach(g => {
    g.sort(compareVerses);
    const last = g[g.length - 1];
    if (last.note) noteEnds.add(last.id);
  });
  return { byVerse, groups, noteEnds };
}

function commit(next, { push = true } = {}) {
  records = next;
  snapshot = buildSnapshot(records);
  save(LIST_KEY(owner), records);
  listeners.forEach(fn => fn());
  if (push) schedulePush();
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export const getSnapshot = () => snapshot;

// ── Who owns the marks ───────────────────────────────────────────────────────
// Called by each page with the signed-in user (or null). Switching to an
// account brings in the guest marks, then syncs.
export function setAccount(user) {
  const next = user?.id && user?.email ? { id: user.id, email: user.email } : null;
  const nextOwner = next ? next.id : GUEST;
  if (loaded && nextOwner === owner && next?.email === account?.email) return;
  loaded = true;
  clearTimeout(pushTimer);
  owner = nextOwner;
  account = next;
  lastFetch = 0;
  retryAfter = 0;
  let list = loadList(owner);
  if (next) {
    const guest = loadList(GUEST);
    if (guest.length) {
      list = adoptRecords(list, guest);
      save(LIST_KEY(GUEST), null);
    }
  }
  commit(list, { push: false });
  if (next) syncNow();
}

// ── Editing ──────────────────────────────────────────────────────────────────
const now = () => new Date().toISOString();

// A record edited now, waiting to be sent.
function stamp(r, patch) {
  return { ...r, ...patch, updated_at: now(), _dirty: true };
}

// Makes one mark from per-verse ranges (see splitSelection). Returns its group id.
export function createGroup(ranges, { style, color, note = null }) {
  const group_id = uuid();
  const at = now();
  const made = ranges
    .map(r => sanitizeRecord({
      id: uuid(), group_id, book: r.book, chapter: r.chapter, verse: r.verse, lang: r.lang,
      start: r.start, end: r.end, quote: r.quote, style, color, note,
      updated_at: at, deleted: false,
    }))
    .filter(Boolean)
    .map(r => ({ ...r, _dirty: true }));
  if (!made.length) return null;
  commit([...records, ...made]);
  return group_id;
}

// Changes style, colour or note on every verse of a mark.
export function updateGroup(groupId, patch) {
  const clean = {};
  if (patch.style) clean.style = patch.style;
  if (patch.color) clean.color = patch.color;
  if ('note' in patch) clean.note = patch.note && patch.note.trim() ? patch.note.slice(0, NOTE_MAX) : null;
  commit(records.map(r => r.group_id === groupId && !r.deleted ? stamp(r, clean) : r));
}

// Guests have nothing to tell a server, so a delete just removes the record;
// for an account it becomes a tombstone that syncs.
export function deleteGroup(groupId) {
  if (!account) {
    commit(records.filter(r => r.group_id !== groupId), { push: false });
    return;
  }
  commit(records.map(r => r.group_id === groupId && !r.deleted
    ? stamp(r, { deleted: true, note: null }) : r));
}

// ── Sync ─────────────────────────────────────────────────────────────────────
function schedulePush() {
  if (!account) return;
  clearTimeout(pushTimer);
  pushTimer = setTimeout(() => syncNow({ fetch: false }), PUSH_DELAY);
}

const toWire = r => {
  const { _dirty, ...rest } = r;
  return rest;
};

async function push(acct) {
  const dirty = records.filter(r => r._dirty);
  for (let i = 0; i < dirty.length; i += PUSH_CHUNK) {
    const chunk = dirty.slice(i, i + PUSH_CHUNK);
    const { error } = await supabase.rpc('save_annotations', {
      p_id: acct.id, p_email: acct.email, p_items: chunk.map(toWire),
    });
    if (error) throw error;
    if (account !== acct) return;          // signed out or switched mid-way
    // Only clear the flag on records not edited again while this was in flight.
    const sent = new Map(chunk.map(r => [r.id, r.updated_at]));
    commit(records.map(r => r._dirty && sent.get(r.id) === r.updated_at
      ? (({ _dirty, ...rest }) => rest)(r) : r), { push: false });
  }
}

async function pull(acct) {
  const since = load(SINCE_KEY(acct.id));
  let from = since ? new Date(Date.parse(since) - OVERLAP_MS).toISOString() : null;
  let newest = since;
  for (;;) {
    const { data, error } = await supabase.rpc('list_annotations', {
      p_id: acct.id, p_email: acct.email, p_since: from,
    });
    if (error) throw error;
    if (account !== acct) return;
    const rows = Array.isArray(data) ? data : [];
    if (rows.length) {
      const { records: merged, changed } = mergeRecords(records, rows);
      // A local copy found newer than the server's is re-flagged; send it.
      if (changed) commit(merged, { push: merged.some(r => r._dirty) });
      const last = rows[rows.length - 1].server_at;
      if (last && (!newest || last > newest)) newest = last;
    }
    if (rows.length < PAGE_SIZE || !newest || newest === from) break;
    from = newest;
  }
  if (newest) save(SINCE_KEY(acct.id), newest);
}

// Sends what changed, then fetches what other devices sent. Sending first
// means a stale local edit is answered by the newer server copy in the same
// round. Calls made while one runs are folded into one more run after it.
export function syncNow({ fetch = true } = {}) {
  const acct = account;
  if (!acct || Date.now() < retryAfter) return Promise.resolve();
  if (syncing) { again = true; return syncing; }
  syncing = (async () => {
    try {
      await push(acct);
      if (fetch && account === acct) { lastFetch = Date.now(); await pull(acct); }
    } catch (err) {
      retryAfter = Date.now() + RETRY_GAP;
      // Most likely the SQL has not been applied yet; say so once, not on
      // every tap. Marks stay saved on this device meanwhile.
      if (!warned) { console.warn('annotations: sync failed, keeping marks on this device', err); warned = true; }
    } finally {
      syncing = null;
      if (again) { again = false; syncNow({ fetch: false }); }
    }
  })();
  return syncing;
}

// ── Browser events ───────────────────────────────────────────────────────────
// Fetch when the reader comes back to the tab (another device may have
// written meanwhile); send when they leave it, rather than wait for the timer.
// Another tab of the app writing the same list is picked up from the storage
// event.
if (typeof window !== 'undefined') {
  const onVisible = () => {
    if (!account) return;
    if (document.visibilityState === 'hidden') { clearTimeout(pushTimer); syncNow({ fetch: false }); return; }
    if (Date.now() - lastFetch > REFETCH_GAP) syncNow();
  };
  window.addEventListener('focus', onVisible);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('storage', e => {
    if (e.key !== LIST_KEY(owner)) return;
    records = loadList(owner);
    snapshot = buildSnapshot(records);
    listeners.forEach(fn => fn());
  });
}
